import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface SmartGoal {
  description: string;
  measurement_criteria: string;
  target_value: number;
}

function SprintCreation() {
  const navigate = useNavigate();
  const [goals, setGoals] = useState<SmartGoal[]>([
    { description: '', measurement_criteria: '', target_value: 0 },
    { description: '', measurement_criteria: '', target_value: 0 },
    { description: '', measurement_criteria: '', target_value: 0 },
  ]);

  const handleGoalChange = (index: number, field: keyof SmartGoal, value: string | number) => {
    const newGoals = [...goals];
    if (field === 'target_value') {
      // Ensure we have a valid number
      const numValue = value === '' ? 0 : Number(value);
      newGoals[index] = { 
        ...newGoals[index], 
        [field]: isNaN(numValue) ? 0 : numValue 
      };
    } else {
      newGoals[index] = { ...newGoals[index], [field]: value };
    }
    setGoals(newGoals);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 90);

    try {
      // Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      // Create the sprint
      const { data: sprint, error: sprintError } = await supabase
        .from('sprints')
        .insert([
          {
            user_id: user.id,
            start_date: startDate.toISOString(),
            end_date: endDate.toISOString(),
            status: 'in_progress'
          }
        ])
        .select()
        .single();

      if (sprintError) throw sprintError;

      // Create the goals
      const goalsToInsert = goals
        .filter(goal => goal.description.trim() !== '')
        .map(goal => ({
          ...goal,
          sprint_id: sprint.id,
          current_value: 0
        }));

      if (goalsToInsert.length > 0) {
        const { error: goalsError } = await supabase
          .from('smart_goals')
          .insert(goalsToInsert);

        if (goalsError) throw goalsError;
      }

      navigate('/');
    } catch (error: any) {
      console.error('Error creating sprint:', error);
      alert(error.message || 'Failed to create sprint. Please try again.');
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl font-bold mb-8">Create New 90-Day Sprint</h1>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-6">
          <h2 className="text-xl font-semibold">SMART Goals</h2>
          {goals.map((goal, index) => (
            <div key={index} className="space-y-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium">Goal {index + 1}</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description
                  <input
                    type="text"
                    value={goal.description}
                    onChange={(e) => handleGoalChange(index, 'description', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  How will you measure this?
                  <input
                    type="text"
                    value={goal.measurement_criteria}
                    onChange={(e) => handleGoalChange(index, 'measurement_criteria', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Target Value
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={goal.target_value}
                    onChange={(e) => handleGoalChange(index, 'target_value', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  />
                </label>
              </div>
            </div>
          ))}
        </div>

        <button
          type="submit"
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Create Sprint
        </button>
      </form>
    </div>
  );
}

export default SprintCreation;